import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import FilterBar, { getDefaultFilters } from "@/components/FilterBar";
import { 
  Download, BarChart3, Users, ClipboardList, GitBranch, FileSpreadsheet,
  Search, MapPin, TrendingUp, TrendingDown, AlertTriangle, Flame, Clock,
  Phone, Mail, Building, Award, Target, DollarSign, UserCheck, Activity,
  ChevronUp, ChevronDown, AlertCircle, CheckCircle2, XCircle, FileDown,
  Info, Zap, UserX
} from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend, ComposedChart, Line
} from "recharts";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

import { API } from "@/config/api";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899"];
const TERRITORY_COLORS = {
  high: "#16a34a",     // Dark green
  moderate: "#eab308", // Yellow
  low: "#ef4444"       // Red
};

import { AREAS, REPS, getRepDisplayName } from "@/config/constants";
import LocationSelector from "@/components/LocationSelector";
const TERRITORIES = AREAS; // Backward compatibility alias

const formatLocation = (area, subregion, suburb) => {
  const parts = [suburb, subregion, area].filter(Boolean);
  return parts.length > 0 ? parts.join(", ") : "-";
};

const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState("exports");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  
  // Initialize filters once with lazy initialization
  const [filters, setFilters] = useState(() => getDefaultFilters());
  
  // Doctor Value Metrics state
  const [doctorMetrics, setDoctorMetrics] = useState(null);
  const [doctorSearch, setDoctorSearch] = useState("");
  const [doctorTerritory, setDoctorTerritory] = useState("");
  const [topDoctors, setTopDoctors] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "total_referrals", direction: "desc" });
  
  // Data Integrity state
  const [dataIntegrity, setDataIntegrity] = useState(null);
  const [integrityDialogOpen, setIntegrityDialogOpen] = useState(false);
  const [integrityDialogType, setIntegrityDialogType] = useState("");
  const [integrityDialogDoctors, setIntegrityDialogDoctors] = useState([]);
  
  // Territory Heatmap state
  const [territoryData, setTerritoryData] = useState(null);
  const [heatmapMetric, setHeatmapMetric] = useState("referrals");
  
  // Rep Scorecard state
  const [repScorecard, setRepScorecard] = useState(null);
  const [scorecardMonth, setScorecardMonth] = useState("");
  const [scorecardTerritory, setScorecardTerritory] = useState("");

  // Build filter params string
  const buildFilterParams = useCallback((currentFilters = {}) => {
    const params = new URLSearchParams();
    if (currentFilters.startDate) params.append("start_date", currentFilters.startDate);
    if (currentFilters.endDate) params.append("end_date", currentFilters.endDate);
    if (currentFilters.territory && currentFilters.territory !== "all") params.append("territory", currentFilters.territory);
    if (currentFilters.rep && currentFilters.rep !== "all") params.append("rep", currentFilters.rep);
    if (currentFilters.doctorId) params.append("doctor_id", currentFilters.doctorId);
    return params.toString();
  }, []);

  const fetchInitialData = useCallback(async (currentFilters = {}) => {
    try {
      setLoading(true);
      const queryString = buildFilterParams(currentFilters);
      const statsUrl = queryString ? `${API}/dashboard/stats?${queryString}` : `${API}/dashboard/stats`;
      
      const [statsRes, topDocsRes, integrityRes] = await Promise.all([
        axios.get(statsUrl),
        axios.get(`${API}/reports/top-doctors?limit=10`),
        axios.get(`${API}/reports/data-integrity`)
      ]);
      setStats(statsRes.data);
      setTopDoctors(topDocsRes.data);
      setDataIntegrity(integrityRes.data);
    } catch (error) {
      toast.error("Failed to fetch report data");
    } finally {
      setLoading(false);
    }
  }, [buildFilterParams]);

  // Initial load only
  useEffect(() => {
    fetchInitialData(filters);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle single filter change (updates UI only, no fetch)
  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  // Apply filters - triggers data fetch
  const handleApplyFilters = useCallback(() => {
    fetchInitialData(filters);
    if (activeTab === "doctor-value") {
      fetchDoctorMetrics();
    }
    if (activeTab === "territory") {
      fetchTerritoryData();
    }
    if (activeTab === "rep-scorecard") {
      fetchRepScorecard();
    }
  }, [filters, fetchInitialData, activeTab]);

  // Clear filters - reset to defaults and fetch
  const handleClearFilters = useCallback(() => {
    const defaults = getDefaultFilters();
    setFilters(defaults);
    fetchInitialData(defaults);
  }, [fetchInitialData]);

  // Handle preset apply - set filters and fetch data
  const handlePresetApply = useCallback((presetFilters) => {
    setFilters(presetFilters);
    fetchInitialData(presetFilters);
  }, [fetchInitialData]);

  // CSV Export function - exports current visible data
  const handleExportCSV = useCallback(() => {
    if (!stats) {
      toast.error("No data to export");
      return;
    }

    try {
      // Build CSV content from current stats
      const rows = [];
      
      // Header info
      rows.push(["RolodexCRM Report"]);
      rows.push(["Generated:", new Date().toISOString().split('T')[0]]);
      rows.push(["Date Range:", `${filters.startDate || 'All'} to ${filters.endDate || 'All'}`]);
      if (filters.territory) rows.push(["Territory:", filters.territory]);
      if (filters.rep) rows.push(["Rep:", `Rep ${filters.rep}`]);
      if (filters.selectedDoctor) rows.push(["Doctor:", filters.selectedDoctor.doctor_name]);
      rows.push([]);
      
      // Summary Stats
      rows.push(["=== SUMMARY STATISTICS ==="]);
      rows.push(["Metric", "Value"]);
      rows.push(["Active Doctors", stats.total_doctors || 0]);
      rows.push(["Total Visits", stats.total_visits || 0]);
      rows.push(["Total Referrals", stats.total_referrals || 0]);
      rows.push(["Commission Ready", stats.commission_eligible || 0]);
      rows.push([]);
      
      // Visits by Rep
      if (stats.visits_by_rep && Object.keys(stats.visits_by_rep).length > 0) {
        rows.push(["=== VISITS BY REP ==="]);
        rows.push(["Rep", "Visits"]);
        Object.entries(stats.visits_by_rep).forEach(([rep, count]) => {
          rows.push([`Rep ${rep}`, count]);
        });
        rows.push([]);
      }
      
      // Visits by Area
      if (stats.visits_by_area && Object.keys(stats.visits_by_area).length > 0) {
        rows.push(["=== VISITS BY AREA ==="]);
        rows.push(["Area", "Visits"]);
        Object.entries(stats.visits_by_area).forEach(([area, count]) => {
          rows.push([area, count]);
        });
        rows.push([]);
      }
      
      // Pipeline by Stage
      if (stats.pipeline_by_stage && Object.keys(stats.pipeline_by_stage).length > 0) {
        rows.push(["=== PIPELINE BY STAGE ==="]);
        rows.push(["Stage", "Cases"]);
        Object.entries(stats.pipeline_by_stage).forEach(([stage, count]) => {
          rows.push([stage, count]);
        });
        rows.push([]);
      }
      
      // Doctors by Area
      if (stats.doctors_by_area && Object.keys(stats.doctors_by_area).length > 0) {
        rows.push(["=== DOCTORS BY AREA ==="]);
        rows.push(["Area", "Count"]);
        Object.entries(stats.doctors_by_area).forEach(([area, count]) => {
          rows.push([area, count]);
        });
      }

      // Convert to CSV string
      const csvContent = rows.map(row => 
        row.map(cell => {
          const str = String(cell);
          // Escape quotes and wrap in quotes if needed
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        }).join(',')
      ).join('\n');

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `RolodexCRM_Report_${dateStr}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success("Report exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export report");
    }
  }, [stats, filters]);

  // Fetch doctor metrics with search/filter
  const fetchDoctorMetrics = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (doctorSearch) params.append("search", doctorSearch);
      if (doctorTerritory) params.append("territory", doctorTerritory);
      
      const response = await axios.get(`${API}/reports/doctor-value-metrics?${params.toString()}`);
      setDoctorMetrics(response.data);
    } catch (error) {
      toast.error("Failed to fetch doctor metrics");
    }
  }, [doctorSearch, doctorTerritory]);

  useEffect(() => {
    if (activeTab === "doctor-value") {
      fetchDoctorMetrics();
    }
  }, [activeTab, fetchDoctorMetrics]);

  // Fetch territory heatmap data
  const fetchTerritoryData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports/territory-heatmap?metric=${heatmapMetric}`);
      setTerritoryData(response.data);
    } catch (error) {
      toast.error("Failed to fetch territory data");
    }
  }, [heatmapMetric]);

  useEffect(() => {
    if (activeTab === "territory") {
      fetchTerritoryData();
    }
  }, [activeTab, fetchTerritoryData]);

  // Fetch rep scorecard data
  const fetchRepScorecard = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (scorecardMonth) params.append("month", scorecardMonth);
      if (scorecardTerritory) params.append("territory", scorecardTerritory);
      
      const response = await axios.get(`${API}/reports/rep-scorecard?${params.toString()}`);
      setRepScorecard(response.data);
    } catch (error) {
      toast.error("Failed to fetch rep scorecard");
    }
  }, [scorecardMonth, scorecardTerritory]);

  useEffect(() => {
    if (activeTab === "rep-scorecard") {
      fetchRepScorecard();
    }
  }, [activeTab, fetchRepScorecard]);

  const handleExport = async (type) => {
    setExporting(type);
    try {
      const response = await axios.get(`${API}/export/${type}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${type} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    } finally {
      setExporting(null);
    }
  };

  const handleExportDataIntegrity = async (issueType = "all") => {
    setExporting(`integrity-${issueType}`);
    try {
      const response = await axios.get(`${API}/export/data-integrity?issue_type=${issueType}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `data_integrity_${issueType}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Data integrity report exported");
    } catch (error) {
      toast.error("Failed to export data integrity report");
    } finally {
      setExporting(null);
    }
  };

  const handleExportRepScorecard = async () => {
    setExporting("rep-scorecard");
    try {
      const params = new URLSearchParams();
      if (scorecardMonth) params.append("month", scorecardMonth);
      if (scorecardTerritory) params.append("territory", scorecardTerritory);
      
      const response = await axios.get(`${API}/export/rep-scorecard?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rep_scorecard.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Rep scorecard exported");
    } catch (error) {
      toast.error("Failed to export rep scorecard");
    } finally {
      setExporting(null);
    }
  };

  const openIntegrityDialog = (type, doctors) => {
    setIntegrityDialogType(type);
    setIntegrityDialogDoctors(doctors);
    setIntegrityDialogOpen(true);
  };

  // Sort doctors
  const sortedDoctors = doctorMetrics?.doctors?.slice().sort((a, b) => {
    const aVal = a[sortConfig.key] || 0;
    const bVal = b[sortConfig.key] || 0;
    return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
  }) || [];

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "desc" ? "asc" : "desc"
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === "asc" ? 
      <ChevronUp className="w-3 h-3 inline ml-1" /> : 
      <ChevronDown className="w-3 h-3 inline ml-1" />;
  };

  // Generate month options
  const monthOptions = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthOptions.push({
      value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
      label: date.toLocaleString('default', { month: 'long', year: 'numeric' })
    });
  }

  const formatChartData = (data) => {
    if (!data) return [];
    return Object.entries(data).map(([name, value]) => ({ name, value }));
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-muted rounded"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-48 bg-muted rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const visitsByRepData = formatChartData(stats?.visits_by_rep);
  const visitsByAreaData = formatChartData(stats?.visits_by_area);
  const pipelineData = formatChartData(stats?.pipeline_by_stage);
  const doctorsByAreaData = formatChartData(stats?.doctors_by_area);
  const materialsData = formatChartData(stats?.materials_used);

  return (
    <div className="p-4 lg:p-6 space-y-4 sm:space-y-6 pb-6" data-testid="reports-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col gap-3"
      >
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Reports & Analytics
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            View performance metrics, doctor intelligence, and export data
          </p>
        </div>
        
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-auto">
            <TabsList className="h-9 w-max">
              <TabsTrigger value="exports" className="text-xs px-3">Exports</TabsTrigger>
              <TabsTrigger value="doctor-value" className="text-xs px-3">Doctor Value</TabsTrigger>
              <TabsTrigger value="data-integrity" className="text-xs px-3">Data Integrity</TabsTrigger>
              <TabsTrigger value="territory" className="text-xs px-3">Territory</TabsTrigger>
              <TabsTrigger value="rep-scorecard" className="text-xs px-3">Rep Scorecard</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </motion.div>

      {/* Filter Bar */}
      <FilterBar 
        filters={filters}
        onFilterChange={handleFilterChange}
        onApply={handleApplyFilters}
        onClear={handleClearFilters}
        onPresetApply={handlePresetApply}
        showDoctorFilter={true}
        showRepFilter={true}
        showTerritoryFilter={true}
        showDateFilter={true}
      />

      {/* CSV Export Button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          onClick={handleExportCSV}
          className="gap-2"
          data-testid="export-csv-button"
        >
          <FileDown className="w-4 h-4" />
          Export to CSV
        </Button>
      </div>

      {/* Tab Content */}
      {activeTab === "exports" && (
        <>
          {/* Export Cards */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <FileSpreadsheet className="w-5 h-5 text-primary" />
                  Export Data
                </CardTitle>
                <CardDescription>Download your data as Excel spreadsheets</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => handleExport('doctors')}
                    disabled={exporting === 'doctors'}
                    data-testid="export-doctors-button"
                  >
                    <Users className="w-6 h-6 text-blue-500" />
                    <span className="font-medium">Doctors</span>
                    <span className="text-xs text-muted-foreground">
                      {exporting === 'doctors' ? 'Exporting...' : 'Export all doctors'}
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => handleExport('visits')}
                    disabled={exporting === 'visits'}
                    data-testid="export-visits-button"
                  >
                    <ClipboardList className="w-6 h-6 text-emerald-500" />
                    <span className="font-medium">Visits</span>
                    <span className="text-xs text-muted-foreground">
                      {exporting === 'visits' ? 'Exporting...' : 'Export all visits'}
                    </span>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-auto py-4 flex flex-col items-center gap-2"
                    onClick={() => handleExport('referrals')}
                    disabled={exporting === 'referrals'}
                    data-testid="export-referrals-button"
                  >
                    <GitBranch className="w-6 h-6 text-amber-500" />
                    <span className="font-medium">Referrals</span>
                    <span className="text-xs text-muted-foreground">
                      {exporting === 'referrals' ? 'Exporting...' : 'Export all referrals'}
                    </span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Summary Stats */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-6 text-center">
                  <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats?.total_doctors || 0}</p>
                  <p className="text-sm text-muted-foreground">Active Doctors</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <ClipboardList className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats?.total_visits || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Visits</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <GitBranch className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                  <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats?.total_referrals || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Referrals</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 text-center">
                  <Download className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>{stats?.commission_eligible || 0}</p>
                  <p className="text-sm text-muted-foreground">Commission Ready</p>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.3 }}>
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Rep Performance</CardTitle>
                  <CardDescription>Number of visits per sales rep</CardDescription>
                </CardHeader>
                <CardContent>
                  {visitsByRepData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={visitsByRepData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <RechartsTooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                        <Bar dataKey="value" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Visits" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">No visit data available</div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.4 }}>
              <Card>
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Visits by Area</CardTitle>
                  <CardDescription>Distribution of visits across territories</CardDescription>
                </CardHeader>
                <CardContent>
                  {visitsByAreaData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie 
                          data={visitsByAreaData} 
                          cx="50%" 
                          cy="50%" 
                          innerRadius={60} 
                          outerRadius={100} 
                          paddingAngle={5} 
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          {visitsByAreaData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">No visit data available</div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </>
      )}

      {activeTab === "doctor-value" && (
        <>
          {/* Top 10 Doctors Widget */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-blue-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Award className="w-5 h-5" />
                  Top 10 High-Value Doctors
                </CardTitle>
                <CardDescription className="text-blue-600">Ranked by referral volume and conversion</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="w-full">
                  <div className="flex gap-3 pb-2">
                    {topDoctors?.top_doctors?.map((doc, idx) => (
                      <Card key={doc.doctor_id} className={cn(
                        "min-w-[200px] flex-shrink-0",
                        idx === 0 && "border-yellow-400 bg-yellow-50",
                        idx === 1 && "border-gray-400 bg-gray-50",
                        idx === 2 && "border-amber-600 bg-amber-50"
                      )}>
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between mb-2">
                            <Badge variant={idx < 3 ? "default" : "secondary"} className={cn(
                              "text-xs",
                              idx === 0 && "bg-yellow-500",
                              idx === 1 && "bg-gray-400",
                              idx === 2 && "bg-amber-600"
                            )}>
                              #{idx + 1}
                            </Badge>
                            {doc.value_tier === "high_value" && <Flame className="w-4 h-4 text-orange-500" />}
                          </div>
                          <p className="font-medium text-sm truncate">{doc.doctor_name}</p>
                          <p className="text-xs text-muted-foreground truncate">{doc.practice_name || doc.city_territory}</p>
                          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <p className="text-muted-foreground">Referrals</p>
                              <p className="font-bold text-blue-600">{doc.total_referrals}</p>
                            </div>
                            <div>
                              <p className="text-muted-foreground">Conversion</p>
                              <p className="font-bold text-emerald-600">{doc.conversion_rate}%</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </CardContent>
            </Card>
          </motion.div>

          {/* Doctor Value Summary Stats */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <Card>
                <CardContent className="p-4 text-center">
                  <Flame className="w-6 h-6 text-orange-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-orange-600">{doctorMetrics?.summary?.high_value || topDoctors?.summary?.high_value || 0}</p>
                  <p className="text-xs text-muted-foreground">High Value (Top 20%)</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <AlertTriangle className="w-6 h-6 text-yellow-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-yellow-600">{doctorMetrics?.summary?.at_risk || 0}</p>
                  <p className="text-xs text-muted-foreground">At Risk (30-90 days)</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Clock className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-amber-600">{doctorMetrics?.summary?.dormant || topDoctors?.summary?.dormant || 0}</p>
                  <p className="text-xs text-muted-foreground">Dormant (90+ days)</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Zap className="w-6 h-6 text-green-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-green-600">{doctorMetrics?.summary?.new || 0}</p>
                  <p className="text-xs text-muted-foreground">New (Visits, no refs)</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Users className="w-6 h-6 text-blue-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-blue-600">{doctorMetrics?.summary?.with_referrals || topDoctors?.summary?.with_referrals || 0}</p>
                  <p className="text-xs text-muted-foreground">With Referrals</p>
                </CardContent>
              </Card>
            </div>
          </motion.div>

          {/* Search and Filter */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <Target className="w-4 h-4 text-primary" />
                  Doctor Value Intelligence
                </CardTitle>
                <CardDescription className="text-xs">Comprehensive metrics per doctor</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by doctor name or practice..."
                      value={doctorSearch}
                      onChange={(e) => setDoctorSearch(e.target.value)}
                      className="pl-10 h-10"
                    />
                  </div>
                  <Select value={doctorTerritory} onValueChange={setDoctorTerritory}>
                    <SelectTrigger className="w-full sm:w-40 h-10">
                      <SelectValue placeholder="Territory" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Territories</SelectItem>
                      {TERRITORIES.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={fetchDoctorMetrics} className="h-10">
                    <Search className="w-4 h-4 mr-2" />
                    Search
                  </Button>
                </div>

                {/* Doctor Metrics Table */}
                <TooltipProvider>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left py-2 px-3 font-medium cursor-pointer hover:bg-muted" onClick={() => handleSort("doctor_name")}>
                            Doctor <SortIcon columnKey="doctor_name" />
                          </th>
                          <th className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted hidden sm:table-cell" onClick={() => handleSort("total_referrals")}>
                            Referrals <SortIcon columnKey="total_referrals" />
                          </th>
                          <th className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted" onClick={() => handleSort("total_completed")}>
                            Completed <SortIcon columnKey="total_completed" />
                          </th>
                          <th className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted" onClick={() => handleSort("conversion_rate")}>
                            Conv. % <SortIcon columnKey="conversion_rate" />
                          </th>
                          <th className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted hidden md:table-cell" onClick={() => handleSort("avg_visit_to_referral")}>
                            <Tooltip>
                              <TooltipTrigger className="flex items-center gap-1 justify-center w-full">
                                Visit→Ref <Info className="w-3 h-3" /> <SortIcon columnKey="avg_visit_to_referral" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Days from first visit to first referral</p>
                              </TooltipContent>
                            </Tooltip>
                          </th>
                          <th className="text-center py-2 px-3 font-medium cursor-pointer hover:bg-muted hidden lg:table-cell" onClick={() => handleSort("days_since_last_visit")}>
                            <Tooltip>
                              <TooltipTrigger className="flex items-center gap-1 justify-center w-full">
                                Days Since <Info className="w-3 h-3" /> <SortIcon columnKey="days_since_last_visit" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Days since last rep visit</p>
                              </TooltipContent>
                            </Tooltip>
                          </th>
                          <th className="text-center py-2 px-3 font-medium hidden lg:table-cell">Last Visit</th>
                          <th className="text-center py-2 px-3 font-medium">
                            <Tooltip>
                              <TooltipTrigger className="flex items-center gap-1 justify-center w-full">
                                Engagement <Info className="w-3 h-3" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs max-w-[200px]">
                                  <strong>High Value:</strong> Top 20% by referrals<br/>
                                  <strong>Active:</strong> Activity within 30 days<br/>
                                  <strong>At Risk:</strong> 30-90 days inactive<br/>
                                  <strong>Dormant:</strong> 90+ days inactive<br/>
                                  <strong>New:</strong> Visits but no referrals yet
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedDoctors.slice(0, 50).map((doc) => (
                          <tr key={doc.doctor_id} className="border-b hover:bg-muted/30">
                            <td className="py-2 px-3">
                              <div>
                                <p className="font-medium truncate max-w-[150px]">{doc.doctor_name}</p>
                                <p className="text-xs text-muted-foreground truncate max-w-[150px]">{doc.practice_name || doc.city_territory}</p>
                              </div>
                            </td>
                            <td className="py-2 px-3 text-center hidden sm:table-cell font-medium">{doc.total_referrals}</td>
                            <td className="py-2 px-3 text-center font-medium text-emerald-600">{doc.total_completed}</td>
                            <td className="py-2 px-3 text-center">
                              <Badge variant={doc.conversion_rate >= 50 ? "default" : doc.conversion_rate > 0 ? "secondary" : "destructive"} className="text-xs">
                                {doc.conversion_rate}%
                              </Badge>
                            </td>
                            <td className="py-2 px-3 text-center hidden md:table-cell">
                              {doc.avg_visit_to_referral !== null && doc.avg_visit_to_referral !== undefined ? (
                                <span className={cn(
                                  "font-medium",
                                  doc.avg_visit_to_referral <= 14 && "text-green-600",
                                  doc.avg_visit_to_referral > 14 && doc.avg_visit_to_referral <= 30 && "text-yellow-600",
                                  doc.avg_visit_to_referral > 30 && "text-red-600"
                                )}>
                                  {doc.avg_visit_to_referral}d
                                </span>
                              ) : "-"}
                            </td>
                            <td className="py-2 px-3 text-center hidden lg:table-cell">
                              {doc.days_since_last_visit !== null && doc.days_since_last_visit !== undefined ? (
                                <span className={cn(
                                  "font-medium",
                                  doc.days_since_last_visit <= 30 && "text-green-600",
                                  doc.days_since_last_visit > 30 && doc.days_since_last_visit <= 90 && "text-yellow-600",
                                  doc.days_since_last_visit > 90 && "text-red-600"
                                )}>
                                  {doc.days_since_last_visit}d
                                </span>
                              ) : "-"}
                            </td>
                            <td className="py-2 px-3 text-center text-xs text-muted-foreground hidden lg:table-cell">
                              {doc.last_visit_date || "-"}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {doc.value_tier === "high_value" && (
                                <Badge className="bg-orange-100 text-orange-700 text-[10px] border border-orange-300">
                                  <Flame className="w-3 h-3 mr-1" /> High Value
                                </Badge>
                              )}
                              {doc.value_tier !== "high_value" && doc.engagement_status === "at_risk" && (
                                <Badge className="bg-yellow-100 text-yellow-700 text-[10px] border border-yellow-300">
                                  <AlertTriangle className="w-3 h-3 mr-1" /> At Risk
                                </Badge>
                              )}
                              {doc.value_tier === "dormant" && (
                                <Badge className="bg-gray-100 text-gray-700 text-[10px] border border-gray-300">
                                  <Clock className="w-3 h-3 mr-1" /> Dormant
                                </Badge>
                              )}
                              {doc.engagement_status === "new" && (
                                <Badge className="bg-green-100 text-green-700 text-[10px] border border-green-300">
                                  <Zap className="w-3 h-3 mr-1" /> New
                                </Badge>
                              )}
                              {doc.value_tier === "zero_conversion" && doc.engagement_status !== "new" && (
                                <Badge className="bg-red-100 text-red-700 text-[10px] border border-red-300">
                                  <XCircle className="w-3 h-3 mr-1" /> Zero Conv.
                                </Badge>
                              )}
                              {doc.value_tier === "standard" && doc.engagement_status === "active" && (
                                <Badge className="bg-blue-100 text-blue-700 text-[10px] border border-blue-300">
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Active
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {sortedDoctors.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground">No doctors found</div>
                    )}
                  </div>
                </TooltipProvider>
              </CardContent>
            </Card>
          </motion.div>
        </>
      )}

      {activeTab === "data-integrity" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Data Integrity Alerts
              </CardTitle>
              <CardDescription>Doctors with missing contact information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Alert Summary Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Card 
                  className={cn(
                    "cursor-pointer hover:shadow-md transition-shadow",
                    dataIntegrity?.missing_email?.count > 0 && "border-amber-300 bg-amber-50"
                  )}
                  onClick={() => dataIntegrity?.missing_email?.count > 0 && openIntegrityDialog("Missing Email", dataIntegrity.missing_email.doctors)}
                >
                  <CardContent className="p-4 text-center">
                    <Mail className="w-6 h-6 text-amber-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-amber-600">{dataIntegrity?.summary?.missing_email_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Missing Email</p>
                  </CardContent>
                </Card>
                <Card 
                  className={cn(
                    "cursor-pointer hover:shadow-md transition-shadow",
                    dataIntegrity?.missing_phone?.count > 0 && "border-orange-300 bg-orange-50"
                  )}
                  onClick={() => dataIntegrity?.missing_phone?.count > 0 && openIntegrityDialog("Missing Phone", dataIntegrity.missing_phone.doctors)}
                >
                  <CardContent className="p-4 text-center">
                    <Phone className="w-6 h-6 text-orange-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-orange-600">{dataIntegrity?.summary?.missing_phone_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Missing Phone</p>
                  </CardContent>
                </Card>
                <Card 
                  className={cn(
                    "cursor-pointer hover:shadow-md transition-shadow",
                    dataIntegrity?.missing_both?.count > 0 && "border-red-300 bg-red-50"
                  )}
                  onClick={() => dataIntegrity?.missing_both?.count > 0 && openIntegrityDialog("Missing Both", dataIntegrity.missing_both.doctors)}
                >
                  <CardContent className="p-4 text-center">
                    <XCircle className="w-6 h-6 text-red-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-red-600">{dataIntegrity?.summary?.missing_both_count || 0}</p>
                    <p className="text-xs text-muted-foreground">Missing Both</p>
                  </CardContent>
                </Card>
                <Card 
                  className={cn(
                    "cursor-pointer hover:shadow-md transition-shadow",
                    dataIntegrity?.missing_territory?.count > 0 && "border-purple-300 bg-purple-50"
                  )}
                  onClick={() => dataIntegrity?.missing_territory?.count > 0 && openIntegrityDialog("Missing Territory", dataIntegrity.missing_territory.doctors)}
                >
                  <CardContent className="p-4 text-center">
                    <MapPin className="w-6 h-6 text-purple-500 mx-auto mb-1" />
                    <p className="text-2xl font-bold text-purple-600">{dataIntegrity?.missing_territory?.count || 0}</p>
                    <p className="text-xs text-muted-foreground">Missing Territory</p>
                  </CardContent>
                </Card>
              </div>

              {/* Export Button */}
              <div className="flex justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => handleExportDataIntegrity("all")}
                  disabled={exporting?.startsWith("integrity")}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  {exporting?.startsWith("integrity") ? "Exporting..." : "Export CSV"}
                </Button>
              </div>

              {/* Summary Bar */}
              <Card className="bg-muted/30">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Total Data Issues</p>
                      <p className="text-xs text-muted-foreground">Out of {dataIntegrity?.total_doctors || 0} active doctors</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold">{dataIntegrity?.summary?.total_issues || 0}</p>
                      <p className="text-xs text-muted-foreground">issues found</p>
                    </div>
                  </div>
                  <Progress 
                    value={dataIntegrity?.total_doctors ? ((dataIntegrity.total_doctors - dataIntegrity.summary.total_issues) / dataIntegrity.total_doctors * 100) : 100} 
                    className="h-2 mt-3"
                  />
                  <p className="text-xs text-muted-foreground mt-1 text-right">
                    {dataIntegrity?.total_doctors ? Math.round((dataIntegrity.total_doctors - dataIntegrity.summary.total_issues) / dataIntegrity.total_doctors * 100) : 100}% data complete
                  </p>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {activeTab === "territory" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <MapPin className="w-5 h-5 text-primary" />
                Territory Performance Heatmap
              </CardTitle>
              <CardDescription>Regional performance visualization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Metric Toggle */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={heatmapMetric === "referrals" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHeatmapMetric("referrals")}
                >
                  Referrals
                </Button>
                <Button
                  variant={heatmapMetric === "conversions" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHeatmapMetric("conversions")}
                >
                  Conversions
                </Button>
                <Button
                  variant={heatmapMetric === "revenue" ? "default" : "outline"}
                  size="sm"
                  onClick={() => setHeatmapMetric("revenue")}
                >
                  Revenue
                </Button>
              </div>

              {/* Heatmap Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                {territoryData?.territories?.map((territory) => (
                  <Card 
                    key={territory.territory}
                    className={cn(
                      "transition-all",
                      territory.performance_tier === "high" && "border-green-400 bg-green-50",
                      territory.performance_tier === "moderate" && "border-yellow-400 bg-yellow-50",
                      territory.performance_tier === "low" && "border-red-400 bg-red-50"
                    )}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-lg">{territory.territory}</span>
                        <div 
                          className={cn(
                            "w-4 h-4 rounded-full",
                            territory.performance_tier === "high" && "bg-green-500",
                            territory.performance_tier === "moderate" && "bg-yellow-500",
                            territory.performance_tier === "low" && "bg-red-500"
                          )}
                        />
                      </div>
                      
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Referrals</span>
                          <span className={cn("font-medium", heatmapMetric === "referrals" && "text-primary font-bold")}>{territory.referrals}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Conv. Rate</span>
                          <span className={cn("font-medium", heatmapMetric === "conversions" && "text-primary font-bold")}>{territory.conversion_rate}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Revenue</span>
                          <span className={cn("font-medium", heatmapMetric === "revenue" && "text-primary font-bold")}>R{territory.estimated_revenue?.toLocaleString()}</span>
                        </div>
                      </div>

                      <Progress 
                        value={territory.percentage_of_max} 
                        className={cn(
                          "h-2 mt-3",
                          territory.performance_tier === "high" && "[&>div]:bg-green-500",
                          territory.performance_tier === "moderate" && "[&>div]:bg-yellow-500",
                          territory.performance_tier === "low" && "[&>div]:bg-red-500"
                        )}
                      />
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-4 text-xs pt-2">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-green-500" />
                  <span>High Performance (70%+)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-yellow-500" />
                  <span>Moderate (40-70%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-red-500" />
                  <span>Low Performance (&lt;40%)</span>
                </div>
              </div>

              {/* Summary */}
              {territoryData?.summary && (
                <Card className="bg-muted/30">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-xs text-muted-foreground">Total Referrals</p>
                        <p className="text-xl font-bold">{territoryData.summary.total_referrals}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total Revenue</p>
                        <p className="text-xl font-bold">R{territoryData.summary.total_revenue?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Avg. Conversion</p>
                        <p className="text-xl font-bold">{territoryData.summary.average_conversion_rate}%</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Top Territory</p>
                        <p className="text-xl font-bold text-green-600">{territoryData.summary.top_territory}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {activeTab === "rep-scorecard" && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <Award className="w-5 h-5 text-amber-500" />
                Rep Performance Scorecard
              </CardTitle>
              <CardDescription>Comprehensive metrics per sales rep</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Select value={scorecardMonth} onValueChange={setScorecardMonth}>
                  <SelectTrigger className="w-full sm:w-48 h-10">
                    <SelectValue placeholder="Select Month" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Time</SelectItem>
                    {monthOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={scorecardTerritory} onValueChange={setScorecardTerritory}>
                  <SelectTrigger className="w-full sm:w-40 h-10">
                    <SelectValue placeholder="Territory" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Territories</SelectItem>
                    {TERRITORIES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={handleExportRepScorecard} disabled={exporting === "rep-scorecard"} className="h-10 gap-2">
                  <Download className="w-4 h-4" />
                  {exporting === "rep-scorecard" ? "Exporting..." : "Export"}
                </Button>
              </div>

              {/* Scorecard Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-center py-2 px-2 font-medium w-12">Rank</th>
                      <th className="text-left py-2 px-3 font-medium">Rep</th>
                      <th className="text-center py-2 px-2 font-medium">Visits</th>
                      <th className="text-center py-2 px-2 font-medium">Referrals</th>
                      <th className="text-center py-2 px-2 font-medium hidden sm:table-cell">Conv. %</th>
                      <th className="text-center py-2 px-2 font-medium hidden md:table-cell">Ref/Visit</th>
                      <th className="text-center py-2 px-2 font-medium hidden lg:table-cell">Follow-up %</th>
                      <th className="text-center py-2 px-2 font-medium hidden lg:table-cell">Doctors</th>
                      <th className="text-center py-2 px-2 font-medium hidden sm:table-cell">Revenue</th>
                      <th className="text-center py-2 px-3 font-medium">Score</th>
                      <th className="text-center py-2 px-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {repScorecard?.scorecards?.map((sc) => (
                      <tr key={sc.rep} className={cn(
                        "border-b",
                        sc.performance_indicator === "top_performer" && "bg-green-50",
                        sc.performance_indicator === "needs_attention" && "bg-red-50"
                      )}>
                        <td className="py-3 px-2 text-center">
                          <Badge variant={sc.rank === 1 ? "default" : "secondary"} className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center",
                            sc.rank === 1 && "bg-yellow-500",
                            sc.rank === 2 && "bg-gray-400",
                            sc.rank === 3 && "bg-amber-600"
                          )}>
                            {sc.rank}
                          </Badge>
                        </td>
                        <td className="py-3 px-3 font-medium">{sc.rep_label}</td>
                        <td className="py-3 px-2 text-center">{sc.total_visits}</td>
                        <td className="py-3 px-2 text-center font-medium">{sc.total_referrals}</td>
                        <td className="py-3 px-2 text-center hidden sm:table-cell">
                          <Badge variant={sc.conversion_rate >= 50 ? "default" : "secondary"} className="text-xs">
                            {sc.conversion_rate}%
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-center hidden md:table-cell">{sc.avg_referrals_per_visit}</td>
                        <td className="py-3 px-2 text-center hidden lg:table-cell">{sc.followup_compliance}%</td>
                        <td className="py-3 px-2 text-center hidden lg:table-cell">{sc.active_doctors}</td>
                        <td className="py-3 px-2 text-center hidden sm:table-cell font-medium text-emerald-600">
                          R{sc.estimated_revenue?.toLocaleString()}
                        </td>
                        <td className="py-3 px-3 text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <Progress value={sc.efficiency_score} className="w-16 h-2" />
                            <span className="text-xs font-medium w-8">{sc.efficiency_score}</span>
                          </div>
                        </td>
                        <td className="py-3 px-3 text-center">
                          {sc.performance_indicator === "top_performer" && (
                            <Badge className="bg-green-100 text-green-700 text-[10px] gap-1">
                              <CheckCircle2 className="w-3 h-3" /> Top
                            </Badge>
                          )}
                          {sc.performance_indicator === "on_track" && (
                            <Badge className="bg-yellow-100 text-yellow-700 text-[10px] gap-1">
                              <Activity className="w-3 h-3" /> On Track
                            </Badge>
                          )}
                          {sc.performance_indicator === "needs_attention" && (
                            <Badge className="bg-red-100 text-red-700 text-[10px] gap-1">
                              <AlertTriangle className="w-3 h-3" /> Attention
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              {repScorecard?.summary && (
                <Card className="bg-muted/30">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-xs text-muted-foreground">Total Visits</p>
                        <p className="text-xl font-bold">{repScorecard.summary.total_visits}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total Referrals</p>
                        <p className="text-xl font-bold">{repScorecard.summary.total_referrals}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total Revenue</p>
                        <p className="text-xl font-bold">R{repScorecard.summary.total_revenue?.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Top Performer</p>
                        <p className="text-xl font-bold text-green-600">Rep {repScorecard.summary.top_performer}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Data Integrity Dialog */}
      <Dialog open={integrityDialogOpen} onOpenChange={setIntegrityDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              {integrityDialogType}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="flex justify-end mb-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleExportDataIntegrity(integrityDialogType.toLowerCase().replace(" ", "_"))}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Export
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left py-2 px-3 font-medium">Doctor</th>
                    <th className="text-left py-2 px-3 font-medium">Practice</th>
                    <th className="text-left py-2 px-3 font-medium">Email</th>
                    <th className="text-left py-2 px-3 font-medium">Phone</th>
                  </tr>
                </thead>
                <tbody>
                  {integrityDialogDoctors.map((doc) => (
                    <tr key={doc.doctor_id} className="border-b hover:bg-red-50">
                      <td className="py-2 px-3 font-medium">{doc.doctor_name}</td>
                      <td className="py-2 px-3 text-muted-foreground">{doc.practice_name || "-"}</td>
                      <td className={cn("py-2 px-3", !doc.doctor_email && "text-red-500 font-medium")}>
                        {doc.doctor_email || "MISSING"}
                      </td>
                      <td className={cn("py-2 px-3", !doc.doctor_phone && "text-red-500 font-medium")}>
                        {doc.doctor_phone || "MISSING"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReportsPage;
